import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight, Save, FileText, User, Building2, Paperclip, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { useCreateClient } from "@/hooks/useCreateClient";
import { useCompanies } from "@/hooks/useCompanies";
import { Client } from "@/types/client";
import { ClientInfoInputs } from "@/components/clients/ClientDialog/ClientInfoInputs";
import { CompanySelector } from "@/components/clients/ClientDialog/CompanySelector";
import { AdditionalInfoTab } from "@/components/clients/ClientDialog/AdditionalInfoTab";
import { PassportInfoTab } from "@/components/clients/ClientDialog/PassportInfoTab";
import { AttachmentsTab } from "@/components/clients/ClientDialog/AttachmentsTab";
type FormData = Omit<Client, "id" | "created_at" | "updated_at" | "client_assignments">;
const getInitialFormData = (): FormData => ({
  name_ar: "",
  name_en: "",
  mobile: "",
  additional_info: "",
  passport_info: {},
  attachments: [],
  status: "waiting",
  company_id: undefined,
  // اختياري - سيتم ربطه بترافليون تلقائياً
  company: "",
  created_by: "",
  creator: undefined,
  companies: undefined,
  employees: undefined
});
const AddClient = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(getInitialFormData);
  const [activeTab, setActiveTab] = useState("basic");
  const {
    data: companies = []
  } = useCompanies();
  const createClientMutation = useCreateClient();
  const handleFormChange = (data: Partial<FormData>) => {
    setFormData(prev => ({
      ...prev,
      ...data
    }));
  };
  const handleCompanyAdded = (companyId: string, companyName: string) => {
    setFormData(prev => ({
      ...prev,
      company_id: companyId,
      company: companyName
    }));
  };
  const handleCompanyChange = (companyId: string) => {
    const selectedCompany = companies.find(c => c.id === companyId);
    setFormData(prev => ({
      ...prev,
      company_id: companyId,
      company: selectedCompany?.name_ar || ""
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name_ar.trim()) {
      toast.error("الرجاء إدخال الاسم باللغة العربية");
      setActiveTab("basic");
      return;
    }
    try {
      const clientData = {
        ...formData,
        status: 'waiting' as Client['status'],
        // تعيين الحالة الافتراضية
        created_by: '' // سيتم تعيينه في الخادم
      };
      await createClientMutation.mutateAsync(clientData);
      toast.success("تم إضافة العميل بنجاح");

      // إعادة تعيين النموذج
      setFormData(getInitialFormData());
      setActiveTab("basic");

      // التوجه إلى صفحة إدارة العملاء
      navigate("/clients");
    } catch (error: any) {
      console.error("Error creating client:", error);
      if (error.message?.includes('duplicate')) {
        toast.error("يوجد عميل بنفس البيانات مسبقاً");
      } else {
        toast.error(error.message || "حدث خطأ أثناء إضافة العميل");
      }
    }
  };
  const tabsConfig = [{
    key: "basic",
    label: "المعلومات الأساسية",
    icon: User,
    component: <div className="space-y-6">
          <CompanySelector companyId={formData.company_id || ""} onCompanyChange={handleCompanyChange} onCompanyAdded={handleCompanyAdded} companies={companies} />
          <ClientInfoInputs formData={formData} onFormChange={handleFormChange} />
        </div>
  }, {
    key: "additional",
    label: "معلومات إضافية",
    icon: FileText,
    component: <AdditionalInfoTab formData={formData} onFormChange={handleFormChange} />
  }, {
    key: "passport",
    label: "معلومات الجواز",
    icon: Building2,
    component: <PassportInfoTab formData={formData} onFormChange={handleFormChange} />
  }, {
    key: "attachments",
    label: "المرفقات",
    icon: Paperclip,
    component: <AttachmentsTab formData={{
      ...formData,
      id: "new-client"
    }} onFormChange={handleFormChange} />
  }];
  return <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/clients")} className="flex items-center gap-2">
            <ArrowRight className="w-4 h-4" />
            العودة للعملاء
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">إضافة عميل جديد</h1>
            
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/clients")}>
            إلغاء
          </Button>
          <Button onClick={handleSubmit} disabled={createClientMutation.isPending} className="flex items-center gap-2">
            {createClientMutation.isPending ? <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                جاري الحفظ...
              </> : <>
                <Save className="w-4 h-4" />
                حفظ العميل
              </>}
          </Button>
        </div>
      </div>

      {/* Main Form Card */}
      <Card className="shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-primary/10">
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-primary" />
            بيانات العميل
          </CardTitle>
        </CardHeader>
        
        <CardContent className="p-6">
          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-6">
                {tabsConfig.map(tab => {
                const Icon = tab.icon;
                return <TabsTrigger key={tab.key} value={tab.key} className="flex items-center gap-2 px-4 py-2">
                      <Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{tab.label}</span>
                    </TabsTrigger>;
              })}
              </TabsList>

              {tabsConfig.map(tab => <TabsContent key={tab.key} value={tab.key} className="mt-6">
                  <div className="min-h-[400px]">
                    {tab.component}
                  </div>
                </TabsContent>)}
            </Tabs>
          </form>
        </CardContent>
      </Card>

      {/* Submit Section */}
      <Card className="mt-6 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              تأكد من صحة جميع البيانات قبل الحفظ
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => navigate("/clients")}>
                إلغاء
              </Button>
              <Button onClick={handleSubmit} disabled={createClientMutation.isPending || !formData.name_ar.trim()} className="flex items-center gap-2">
                {createClientMutation.isPending ? <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    جاري الحفظ...
                  </> : <>
                    <Save className="w-4 h-4" />
                    حفظ العميل
                  </>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
};
export default AddClient;